// Production backend URL - API subdomain
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.afya.lipay.store';
// For local development, use: REACT_APP_API_URL=http://localhost:8000 npm start

const apiService = {
    simulateNetworkDelay: () => new Promise(resolve => setTimeout(resolve, 300 + Math.random() * 700)),

    getAuthToken: () => {
        return localStorage.getItem('lipay_afya_token');
    },

    getTransactions: async ({ organizationId, startDate, endDate, patientId } = {}) => {
        const query = `
            query GetTransactions($organizationId: Int, $patientId: String, $startDate: DateTime, $endDate: DateTime) {
                transactions(organizationId: $organizationId, patientId: $patientId, startDate: $startDate, endDate: $endDate) {
                    id
                    patientId
                    userId
                    organizationId
                    totalAmount
                    paymentMethod
                    paymentStatus
                    createdAt
                    items { id inventoryItemId quantity unitPrice }
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(query, { organizationId: organizationId || null, startDate: startDate || null, endDate: endDate || null, patientId: patientId || null });
            return data.transactions || [];
        } catch (error) {
            console.error('Error fetching transactions:', error);
            throw error;
        }
    },

    updateTransaction: async ({ id, paymentMethod, totalAmount }) => {
        const mutation = `
            mutation UpdateTransaction($id: Int!, $paymentMethod: String, $totalAmount: Float) {
                updateTransaction(id: $id, paymentMethod: $paymentMethod, totalAmount: $totalAmount) { id paymentMethod totalAmount paymentStatus }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { id, paymentMethod: paymentMethod || null, totalAmount: totalAmount || null });
            return data.updateTransaction;
        } catch (error) {
            console.error('Error updating transaction:', error);
            throw error;
        }
    },

    updateTransactionStatus: async ({ id, paymentStatus }) => {
        const mutation = `
            mutation UpdateTransactionStatus($id: Int!, $paymentStatus: String!) {
                updateTransaction(id: $id, paymentStatus: $paymentStatus) { id paymentStatus }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { id, paymentStatus });
            return data.updateTransaction;
        } catch (error) {
            console.error('Error updating transaction status:', error);
            throw error;
        }
    },

    // Superadmin & Admin APIs
    getOrganizations: async () => {
        const query = `
            query GetOrganizations {
                organizations { id name orgType createdAt }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(query);
            return data.organizations || [];
        } catch (error) {
            console.error('Error fetching organizations:', error);
            throw error;
        }
    },

    createOrganization: async ({ name, orgType, adminUsername, adminPassword, adminEmail }) => {
        const mutation = `
            mutation CreateOrganization($name: String!, $orgType: String!, $adminUsername: String!, $adminPassword: String!, $adminEmail: String!) {
                createOrganization(name: $name, orgType: $orgType, adminUsername: $adminUsername, adminPassword: $adminPassword, adminEmail: $adminEmail) {
                    id
                    name
                    orgType
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { 
                name, 
                orgType, 
                adminUsername, 
                adminPassword, 
                adminEmail 
            });
            return data.createOrganization;
        } catch (error) {
            console.error('Error creating organization:', error);
            throw error;
        }
    },

    getUsers: async ({ organizationId } = {}) => {
        const query = `
            query GetUsers($organizationId: Int) {
                users(organizationId: $organizationId) { id username email role isActive organizationId }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(query, { organizationId: organizationId || null });
            return data.users || [];
        } catch (error) {
            console.error('Error fetching users:', error);
            throw error;
        }
    },

    inviteUser: async ({ email, role, organizationId, name, username, password }) => {
        // Falls back sensibly if username/password not provided
        const finalUsername = username || email;
        const finalPassword = password || 'ChangeMe123!';
        const mutation = `
            mutation CreateUser($username: String!, $password: String!, $role: String!, $organizationId: Int, $name: String) {
                createUser(username: $username, password: $password, role: $role, organizationId: $organizationId, name: $name) {
                    id username role organizationId
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, {
                username: finalUsername,
                password: finalPassword,
                role,
                organizationId: organizationId || null,
                name: name || null,
            });
            return data.createUser;
        } catch (error) {
            console.error('Error inviting user:', error);
            throw error;
        }
    },

    // Billing & Subscriptions
    getModulePricing: async () => {
        const query = `
            query GetModulePricing {
                modulePricing { id moduleName displayName description monthlyPrice yearlyPrice }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(query);
            return data.modulePricing || [];
        } catch (error) {
            console.error('Error fetching module pricing:', error);
            throw error;
        }
    },

    createUpgradeRequest: async ({ modulesRequested, requestNotes }) => {
        const mutation = `
            mutation CreateUpgradeRequest($input: CreateUpgradeRequestInput!) {
                createUpgradeRequest(input: $input) {
                    id status quotedPrice
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, {
                input: { modulesRequested, requestNotes }
            });
            return data.createUpgradeRequest;
        } catch (error) {
            console.error('Error creating upgrade request:', error);
            throw error;
        }
    },

    getUpgradeRequests: async ({ status } = {}) => {
        const query = `
            query GetUpgradeRequests($status: String) {
                upgradeRequests(status: $status) {
                    id organizationId requestType modulesRequested status quotedPrice requestedAt requestNotes
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(query, { status: status || null });
            return data.upgradeRequests || [];
        } catch (error) {
            console.error('Error fetching upgrade requests:', error);
            throw error;
        }
    },

    approveUpgradeRequest: async ({ requestId, adminNotes }) => {
        const mutation = `
            mutation ApproveUpgrade($requestId: Int!, $adminNotes: String) {
                approveUpgradeRequest(requestId: $requestId, adminNotes: $adminNotes) {
                    id status
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { requestId, adminNotes: adminNotes || null });
            return data.approveUpgradeRequest;
        } catch (error) {
            console.error('Error approving upgrade:', error);
            throw error;
        }
    },

    rejectUpgradeRequest: async ({ requestId, adminNotes }) => {
        const mutation = `
            mutation RejectUpgrade($requestId: Int!, $adminNotes: String) {
                rejectUpgradeRequest(requestId: $requestId, adminNotes: $adminNotes)
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { requestId, adminNotes: adminNotes || null });
            return data.rejectUpgradeRequest;
        } catch (error) {
            console.error('Error rejecting upgrade:', error);
            throw error;
        }
    },

    // Invoice Management
    getInvoices: async ({ organizationId, status } = {}) => {
        const query = `
            query GetInvoices($organizationId: Int, $status: String) {
                invoices(organizationId: $organizationId, status: $status) {
                    id organizationId invoiceNumber invoiceDate dueDate subtotal taxAmount discountAmount totalAmount currency status paidAmount paidAt paymentMethod paymentReference notes lineItems
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(query, { organizationId: organizationId || null, status: status || null });
            return data.invoices || [];
        } catch (error) {
            console.error('Error fetching invoices:', error);
            throw error;
        }
    },

    createInvoice: async ({ organizationId, lineItems, subtotal, dueDate, notes }) => {
        const mutation = `
            mutation CreateInvoice($organizationId: Int!, $lineItems: [String!]!, $subtotal: Float!, $dueDate: DateTime!, $notes: String) {
                createInvoice(organizationId: $organizationId, lineItems: $lineItems, subtotal: $subtotal, dueDate: $dueDate, notes: $notes) {
                    id invoiceNumber totalAmount status
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { organizationId, lineItems, subtotal, dueDate, notes: notes || null });
            return data.createInvoice;
        } catch (error) {
            console.error('Error creating invoice:', error);
            throw error;
        }
    },

    markInvoicePaid: async ({ invoiceId, paymentMethod, paymentReference }) => {
        const mutation = `
            mutation MarkInvoicePaid($invoiceId: Int!, $paymentMethod: String!, $paymentReference: String!) {
                markInvoicePaid(invoiceId: $invoiceId, paymentMethod: $paymentMethod, paymentReference: $paymentReference) {
                    id invoiceNumber status paidAt
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { invoiceId, paymentMethod, paymentReference });
            return data.markInvoicePaid;
        } catch (error) {
            console.error('Error marking invoice paid:', error);
            throw error;
        }
    },

    generateMonthlyInvoices: async () => {
        const mutation = `
            mutation GenerateMonthlyInvoices {
                generateMonthlyInvoices
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation);
            return data.generateMonthlyInvoices;
        } catch (error) {
            console.error('Error generating monthly invoices:', error);
            throw error;
        }
    },

    suspendOverdueAccounts: async () => {
        const mutation = `
            mutation SuspendOverdueAccounts {
                suspendOverdueAccounts
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation);
            return data.suspendOverdueAccounts;
        } catch (error) {
            console.error('Error suspending overdue accounts:', error);
            throw error;
        }
    },

    graphqlRequest: async (query, variables = {}) => {
        await apiService.simulateNetworkDelay();
        const token = apiService.getAuthToken();

        const headers = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/graphql`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({ query, variables }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('GraphQL HTTP Error Response Status:', response.status);
                console.error('GraphQL HTTP Error Details:', errorData);
                const errorMessage = errorData.detail || (errorData.errors ? errorData.errors.map(err => err.message).join(', ') : 'Unknown HTTP error');

                if (response.status === 401) {
                    // Clear all authentication data
                    localStorage.removeItem('lipay_afya_token');
                    localStorage.removeItem('user_organization_id');
                    localStorage.removeItem('lipay_afya_user');
                    
                    // Log for debugging
                    console.log('🔒 Session expired or unauthorized. Redirecting to login...');
                    
                    // Redirect to login page after a brief delay
                    setTimeout(() => {
                        window.location.href = '/';
                    }, 500);
                    
                    throw new Error('Session expired. Redirecting to login...');
                }

                throw new Error(`GraphQL request failed with status ${response.status}: ${errorMessage}`);
            }

            const result = await response.json();

            if (result.errors) {
                console.error('GraphQL Errors:', result.errors);
                const errorMessage = result.errors.map(err => err.message).join(', ');
                throw new Error(`GraphQL errors: ${errorMessage}`);
            }

            return result.data;
        } catch (error) {
            console.error('Network or GraphQL parsing error:', error);
            throw error;
        }
    },

    login: async (username, password) => {
        await apiService.simulateNetworkDelay();
        const formData = new URLSearchParams();
        formData.append('username', username);
        formData.append('password', password);

        try {
            const response = await fetch(`${API_BASE_URL}/token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: formData.toString(),
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Login failed:', errorData);
                throw new Error(errorData.detail || 'Login failed');
            }

            const data = await response.json();
            localStorage.setItem('lipay_afya_token', data.access_token);

            try {
                const tokenParts = data.access_token.split('.');
                if (tokenParts.length === 3) {
                    const payload = JSON.parse(atob(tokenParts[1]));
                    if (payload.organization_id) {
                        localStorage.setItem('user_organization_id', payload.organization_id);
                    } else {
                        console.warn("JWT payload does not contain 'organization_id'.");
                        localStorage.removeItem('user_organization_id');
                    }
                } else {
                    console.warn("Invalid JWT format.");
                    localStorage.removeItem('user_organization_id');
                }
            } catch (decodeError) {
                console.error("Error decoding JWT:", decodeError);
                localStorage.removeItem('user_organization_id');
            }

            return data;
        } catch (error) {
            console.error('Error during login:', error);
            throw error;
        }
    },

    getCurrentUser: async () => {
        const query = `
            query GetCurrentUser {
                me {
                    id
                    username
                    email
                    role
                    isActive
                    organizationId
                }
                myOrganization {
                    id
                    name
                    orgType
                    address
                    phone
                    email
                    enabledFeatures
                }
            }
        `;

        try {
            const data = await apiService.graphqlRequest(query);
            return {
                ...data.me,
                organization: data.myOrganization
            };
        } catch (error) {
            console.error('Error fetching current user:', error);
            throw error;
        }
    },

    // Role-specific API methods
    getMyAppointmentsToday: async (date = null) => {
        const query = `
            query GetMyAppointmentsToday($date: String) {
                myAppointmentsToday(date: $date) {
                    id
                    patientId
                    doctorId
                    department
                    appointmentTime
                    status
                    appointmentType
                    notes
                    priority
                    duration
                    patient {
                        id
                        name
                        age
                        phone
                        symptoms
                    }
                }
            }
        `;

        try {
            const data = await apiService.graphqlRequest(query, { date });
            return data.myAppointmentsToday;
        } catch (error) {
            console.error('Error fetching today\'s appointments:', error);
            throw error;
        }
    },

    getMyPatients: async (limit = 50) => {
        const query = `
            query GetMyPatients($limit: Int) {
                myPatients(limit: $limit) {
                    id
                    name
                    age
                    phone
                    department
                    symptoms
                    status
                    medicalHistory
                }
            }
        `;

        try {
            const data = await apiService.graphqlRequest(query, { limit });
            return data.myPatients;
        } catch (error) {
            console.error('Error fetching my patients:', error);
            throw error;
        }
    },

    getWaitingPatients: async () => {
        const query = `
            query GetWaitingPatients {
                waitingPatients {
                    id
                    name
                    age
                    phone
                    department
                    symptoms
                    status
                    medicalHistory
                }
            }
        `;

        try {
            const data = await apiService.graphqlRequest(query);
            return data.waitingPatients;
        } catch (error) {
            console.error('Error fetching waiting patients:', error);
            throw error;
        }
    },

    getPendingPrescriptions: async () => {
        const query = `
            query GetPendingPrescriptions {
                pendingPrescriptions {
                    id
                    patientId
                    recordDate
                    diagnosis
                    treatment
                    medicationsPrescribed
                    notes
                    patient {
                        id
                        name
                        age
                        phone
                    }
                }
            }
        `;

        try {
            const data = await apiService.graphqlRequest(query);
            return data.pendingPrescriptions;
        } catch (error) {
            console.error('Error fetching pending prescriptions:', error);
            throw error;
        }
    },

    getSettings: async (organizationId) => {
        const query = `
            query GetOrganizationSettings($organizationId: Int!) {
                organization(organizationId: $organizationId) {
                    id
                    name
                    orgType
                    address
                    phone
                    email
                }
            }
        `;

        if (!organizationId) {
            console.warn("No organizationId provided. Returning default settings.");
            return {
                id: null,
                practiceSize: 'clinic',
                enableStaffManagement: true,
                enableInventoryManagement: true,
                enableAdvancedReports: true,
                enableTelemedicine: true,
                enableDetailedMedicalHistory: true,
                organizationName: 'Default Hospital Name',
                organizationLogoUrl: 'https://placehold.co/40x40/007bff/ffffff?text=HL',
                address: null,
                phone: null,
                email: null,
            };
        }

        try {
            const data = await apiService.graphqlRequest(query, { organizationId });
            const organization = data.organization;

            if (organization) {
                const orgType = organization.orgType || 'clinic';
                
                // Set features based on organization type
                const features = {
                    solo: {
                        enableStaffManagement: false,
                        enableInventoryManagement: false,
                        enableAdvancedReports: false,
                        enableTelemedicine: true,
                        enableDetailedMedicalHistory: true,
                    },
                    clinic: {
                        enableStaffManagement: true,
                        enableInventoryManagement: true,
                        enableAdvancedReports: true,
                        enableTelemedicine: true,
                        enableDetailedMedicalHistory: true,
                    },
                    hospital: {
                        enableStaffManagement: true,
                        enableInventoryManagement: true,
                        enableAdvancedReports: true,
                        enableTelemedicine: true,
                        enableDetailedMedicalHistory: true,
                    },
                    pharmacy: {
                        enableStaffManagement: true,
                        enableInventoryManagement: true,
                        enableAdvancedReports: true,
                        enableTelemedicine: false,
                        enableDetailedMedicalHistory: false,
                    },
                };
                
                return {
                    id: organization.id,
                    practiceSize: orgType,
                    ...(features[orgType] || features.clinic),
                    organizationName: organization.name,
                    organizationLogoUrl: 'https://placehold.co/40x40/007bff/ffffff?text=HL',
                    address: organization.address,
                    phone: organization.phone,
                    email: organization.email,
                };
            } else {
                console.warn(`Organization with ID ${organizationId} not found. Falling back to default settings.`);
                return {
                    id: organizationId,
                    practiceSize: 'clinic',
                    enableStaffManagement: true,
                    enableInventoryManagement: true,
                    enableAdvancedReports: true,
                    enableTelemedicine: true,
                    enableDetailedMedicalHistory: true,
                    organizationName: 'Default Hospital Name',
                    organizationLogoUrl: 'https://placehold.co/40x40/007bff/ffffff?text=HL',
                    address: null,
                    phone: null,
                    email: null,
                };
            }
        } catch (error) {
            console.error("Error fetching settings from backend:", error);
            return {
                id: organizationId,
                practiceSize: 'clinic',
                enableStaffManagement: true,
                enableInventoryManagement: true,
                enableAdvancedReports: true,
                enableTelemedicine: true,
                enableDetailedMedicalHistory: true,
                organizationName: 'Default Hospital Name',
                organizationLogoUrl: 'https://placehold.co/40x40/007bff/ffffff?text=HL',
                address: null,
                phone: null,
                email: null,
            };
        }
    },

    updateSettings: async (organizationId, settingsData) => {
        const mutation = `
            mutation UpdateOrganization(
                $id: Int!,
                $name: String,
                $address: String,
                $phone: String,
                $email: String
            ) {
                updateOrganization(
                    id: $id,
                    name: $name,
                    address: $address,
                    phone: $phone,
                    email: $email
                ) {
                    id
                    name
                    orgType
                    address
                    phone
                    email
                }
            }
        `;

        if (!organizationId) {
            throw new Error("Cannot update settings: No organizationId provided.");
        }

        try {
            const data = await apiService.graphqlRequest(mutation, {
                id: organizationId,
                name: settingsData.organizationName,
                address: settingsData.address,
                phone: settingsData.phone,
                email: settingsData.email,
            });
            return {
                id: data.updateOrganization.id,
                organizationName: data.updateOrganization.name,
                practiceSize: data.updateOrganization.orgType, // Read from DB, not settingsData
                organizationLogoUrl: settingsData.organizationLogoUrl || 'https://placehold.co/40x40/007bff/ffffff?text=HL',
                address: data.updateOrganization.address,
                phone: data.updateOrganization.phone,
                email: data.updateOrganization.email,
                enableStaffManagement: settingsData.enableStaffManagement,
                enableInventoryManagement: settingsData.enableInventoryManagement,
                enableAdvancedReports: settingsData.enableAdvancedReports,
                enableTelemedicine: settingsData.enableTelemedicine,
                enableDetailedMedicalHistory: settingsData.enableDetailedMedicalHistory,
            };
        } catch (error) {
            console.error("Error updating settings on backend:", error);
            throw error;
        }
    },

    getPatients: async () => {
        const query = `
            query GetPatients($organizationId: Int) {
                patients(organizationId: $organizationId) {
                    id
                    patientId
                    name
                    age
                    phone
                    email
                    address
                    gender
                    emergencyContact
                    department
                    symptoms
                    medicalHistory
                    organizationId
                    isDeleted
                    status
                }
            }
        `;
        const userOrgId = localStorage.getItem('user_organization_id')
            ? parseInt(localStorage.getItem('user_organization_id'))
            : null;
        if (!userOrgId) {
            console.warn("No user_organization_id found for getPatients. Returning empty array.");
            return [];
        }
        try {
            const data = await apiService.graphqlRequest(query, { organizationId: userOrgId });
            return data.patients.map((p) => ({
                ...p,
                waitTime: '0 min',
            }));
        } catch (error) {
            console.error("Error fetching patients:", error);
            throw error;
        }
    },

    addPatient: async (patientData) => {
        const mutation = `
            mutation CreatePatient(
                $name: String!,
                $age: Int,
                $phone: String,
                $email: String,
                $address: String,
                $gender: String,
                $emergencyContact: String,
                $department: String,
                $symptoms: String,
                $medicalHistory: String,
                $organizationId: Int
            ) {
                createPatient(
                    name: $name,
                    age: $age,
                    phone: $phone,
                    email: $email,
                    address: $address,
                    gender: $gender,
                    emergencyContact: $emergencyContact,
                    department: $department,
                    symptoms: $symptoms,
                    medicalHistory: $medicalHistory,
                    organizationId: $organizationId
                ) {
                    id
                    patientId
                    name
                    age
                    phone
                    email
                    address
                    gender
                    emergencyContact
                    department
                    symptoms
                    medicalHistory
                    organizationId
                    isDeleted
                }
            }
        `;
        const userOrgId = localStorage.getItem('user_organization_id')
            ? parseInt(localStorage.getItem('user_organization_id'))
            : null;
        if (!userOrgId) {
            throw new Error("Cannot add patient: No user_organization_id found in localStorage.");
        }
        try {
            const { userId, ...dataToSend } = patientData;
            const data = await apiService.graphqlRequest(mutation, {
                ...dataToSend,
                organizationId: userOrgId,
            });
            return {
                ...data.createPatient,
                status: 'waiting',
                waitTime: '0 min',
            };
        } catch (error) {
            console.error("Error adding patient:", error);
            throw error;
        }
    },

    updatePatient: async (updatedPatient) => {
        const mutation = `
            mutation UpdatePatient(
                $patientId: String!,
                $name: String,
                $age: Int,
                $phone: String,
                $email: String,
                $address: String,
                $gender: String,
                $emergencyContact: String,
                $department: String,
                $symptoms: String,
                $medicalHistory: String
            ) {
                updatePatient(
                    patientId: $patientId,
                    name: $name,
                    age: $age,
                    phone: $phone,
                    email: $email,
                    address: $address,
                    gender: $gender,
                    emergencyContact: $emergencyContact,
                    department: $department,
                    symptoms: $symptoms,
                    medicalHistory: $medicalHistory
                ) {
                    id
                    patientId
                    name
                    age
                    phone
                    email
                    address
                    gender
                    emergencyContact
                    department
                    symptoms
                    medicalHistory
                    organizationId
                    isDeleted
                }
            }
        `;
        try {
            // Transform the data: use patientId (custom string ID) instead of id (database ID)
            const { id, ...patientData } = updatedPatient;
            const variables = {
                ...patientData,
                patientId: updatedPatient.patientId // Use the custom patient ID string
            };
            
            const data = await apiService.graphqlRequest(mutation, variables);
            return {
                ...data.updatePatient,
                status: updatedPatient.status,
                waitTime: updatedPatient.waitTime,
            };
        } catch (error) {
            console.error("Error updating patient:", error);
            throw error;
        }
    },

    getAppointments: async () => {
        const query = `
            query GetAppointments($organizationId: Int) {
                appointments(organizationId: $organizationId) {
                    id
                    department
                    appointmentTime
                    duration
                    priority
                    isRecurring
                    recurrencePattern
                    status
                    appointmentType
                    notes
                    organizationId
                    isDeleted
                    patient {
                        id
                        patientId
                        name
                    }
                    doctor {
                        id
                        name
                    }
                }
            }
        `;

        const userOrgId = localStorage.getItem('user_organization_id')
            ? parseInt(localStorage.getItem('user_organization_id'))
            : null;

        if (!userOrgId) {
            console.warn("No user_organization_id found for getAppointments. Returning empty array.");
            return [];
        }

        try {
            const data = await apiService.graphqlRequest(query, { organizationId: userOrgId });

            return data.appointments.map(appt => ({
                ...appt,
                date: new Date(appt.appointmentTime).toISOString().split('T')[0],
                time: new Date(appt.appointmentTime).toTimeString().split(' ')[0].substring(0, 5),
                patientName: appt.patient?.name || `Patient ${appt.patient?.id}`,
                patientCode: appt.patient?.patientId || 'N/A',
                doctorName: appt.doctor?.name || `Doctor ${appt.doctor?.id}`,
                doctorId: appt.doctor?.id || null,
                priority: appt.priority,
                duration: appt.duration,
                isRecurring: appt.isRecurring,
                recurrencePattern: appt.recurrencePattern,
                isDeleted: appt.isDeleted,
            }));
        } catch (error) {
            console.error("Error fetching appointments:", error);
            throw error;
        }
    },

    addAppointment: async (appointmentData) => {
        const mutation = `
            mutation CreateAppointment(
                $patientId: String!,
                $doctorId: Int!,
                $appointmentTime: DateTime!,
                $duration: Int,
                $priority: String,
                $isRecurring: Boolean,
                $recurrencePattern: String,
                $department: String,
                $appointmentType: String,
                $status: String,
                $notes: String,
                $organizationId: Int
            ) {
                createAppointment(
                    patientId: $patientId,
                    doctorId: $doctorId,
                    appointmentTime: $appointmentTime,
                    duration: $duration,
                    priority: $priority,
                    isRecurring: $isRecurring,
                    recurrencePattern: $recurrencePattern,
                    department: $department,
                    appointmentType: $appointmentType,
                    status: $status,
                    notes: $notes,
                    organizationId: $organizationId
                ) {
                    id
                    patientId
                    doctorId
                    department
                    appointmentTime
                    duration
                    priority
                    isRecurring
                    recurrencePattern
                    status
                    appointmentType
                    notes
                    organizationId
                    isDeleted
                    patient { id name }
                    doctor { id name }
                }
            }
        `;

        const userOrgId = localStorage.getItem('user_organization_id')
            ? parseInt(localStorage.getItem('user_organization_id'))
            : null;

        if (!userOrgId) {
            throw new Error("Cannot add appointment: No user_organization_id found in localStorage.");
        }

        try {
            const data = await apiService.graphqlRequest(mutation, {
                patientId: appointmentData.patientId,
                doctorId: parseInt(appointmentData.doctorId, 10),
                appointmentTime: appointmentData.appointmentTime,
                duration: appointmentData.duration || null,
                priority: appointmentData.priority || null,
                isRecurring: appointmentData.isRecurring || false,
                recurrencePattern: appointmentData.recurrencePattern || null,
                department: appointmentData.department,
                appointmentType: appointmentData.appointmentType,
                status: appointmentData.status || "scheduled",
                notes: appointmentData.notes || null,
                organizationId: userOrgId,
            });
            return {
                ...data.createAppointment,
                patient: data.createAppointment.patient?.name || `Patient ${data.createAppointment.patientId}`,
                doctor: data.createAppointment.doctor?.name || `Doctor ${data.createAppointment.doctorId}`,
                date: new Date(data.createAppointment.appointmentTime).toISOString().split('T')[0],
                time: new Date(data.createAppointment.appointmentTime).toTimeString().split(' ')[0].substring(0, 5),
                notes: data.createAppointment.notes || null,
            };
        } catch (error) {
            console.error("Error adding appointment:", error);
            throw error;
        }
    },

    updateAppointment: async (updatedAppointment) => {
        const mutation = `
            mutation UpdateAppointment(
                $id: Int!,
                $patientId: String,
                $doctorId: Int,
                $department: String,
                $appointmentTime: DateTime,
                $duration: Int,
                $priority: String,
                $isRecurring: Boolean,
                $recurrencePattern: String,
                $status: String,
                $appointmentType: String,
                $notes: String
            ) {
                updateAppointment(
                    id: $id,
                    patientId: $patientId,
                    doctorId: $doctorId,
                    department: $department,
                    appointmentTime: $appointmentTime,
                    duration: $duration,
                    priority: $priority,
                    isRecurring: $isRecurring,
                    recurrencePattern: $recurrencePattern,
                    status: $status,
                    appointmentType: $appointmentType,
                    notes: $notes
                ) {
                    id
                    patientId
                    doctorId
                    department
                    appointmentTime
                    duration
                    priority
                    isRecurring
                    recurrencePattern
                    status
                    appointmentType
                    notes
                    organizationId
                    isDeleted
                    patient { id name }
                    doctor { id name }
                }
            }
        `;

        const appointmentTimeISO = updatedAppointment.date && updatedAppointment.time
            ? new Date(`${updatedAppointment.date}T${updatedAppointment.time}:00`).toISOString()
            : updatedAppointment.appointmentTime || null;

        try {
            const data = await apiService.graphqlRequest(mutation, {
                id: parseInt(updatedAppointment.id, 10),
                patientId: updatedAppointment.patientId ? String(updatedAppointment.patientId) : null,
                doctorId: updatedAppointment.doctorId ? parseInt(updatedAppointment.doctorId, 10) : null,
                department: updatedAppointment.department || null,
                appointmentTime: appointmentTimeISO,
                duration: updatedAppointment.duration || null,
                priority: updatedAppointment.priority || null,
                isRecurring: updatedAppointment.isRecurring || false,
                recurrencePattern: updatedAppointment.recurrencePattern || null,
                status: updatedAppointment.status || null,
                appointmentType: updatedAppointment.appointmentType || null,
                notes: updatedAppointment.notes || null,
            });
            return {
                ...data.updateAppointment,
                date: updatedAppointment.date || new Date(data.updateAppointment.appointmentTime).toISOString().split('T')[0],
                time: updatedAppointment.time || new Date(data.updateAppointment.appointmentTime).toTimeString().split(' ')[0].substring(0, 5),
                patient: data.updateAppointment.patient?.name || updatedAppointment.patient || `Patient ${data.updateAppointment.patientId}`,
                doctor: data.updateAppointment.doctor?.name || updatedAppointment.doctor || `Doctor ${data.updateAppointment.doctorId}`,
                notes: data.updateAppointment.notes || null,
            };
        } catch (error) {
            console.error("Error updating appointment:", error);
            throw error;
        }
    },

    getStaff: async () => {
        const query = `
            query GetStaffMembers($organizationId: Int) {
                staffMembers(organizationId: $organizationId) {
                    id
                    name
                    phone
                    email
                    specialization
                    department
                    status
                    userId
                    organizationId
                    isDeleted
                    user { role }
                }
            }
        `;
        const userOrgId = localStorage.getItem('user_organization_id')
            ? parseInt(localStorage.getItem('user_organization_id'))
            : null;
        if (!userOrgId) {
            console.warn("No user_organization_id found for getStaff. Returning empty array.");
            return [];
        }
        try {
            const data = await apiService.graphqlRequest(query, { organizationId: userOrgId });
            return data.staffMembers
                .filter((s) => !s.isDeleted)
                .map((s) => ({
                    ...s,
                    role: s.user?.role || null,
                }));
        } catch (error) {
            console.error("Error fetching staff:", error);
            throw error;
        }
    },

    addStaffMember: async (staffData) => {
        const mutation = `
            mutation CreateStaff(
                $name: String!,
                $role: String!,
                $specialization: String,
                $department: String!,
                $organizationId: Int!,
                $username: String!,
                $password: String!,
                $phone: String!,
                $email: String,
                $hireDate: DateTime,
                $salary: Float,
                $workSchedule: String
            ) {
                createStaff(
                    name: $name,
                    role: $role,
                    specialization: $specialization,
                    department: $department,
                    organizationId: $organizationId,
                    username: $username,
                    password: $password,
                    phone: $phone,
                    email: $email,
                    hireDate: $hireDate,
                    salary: $salary,
                    workSchedule: $workSchedule
                ) {
                    id
                    name
                    phone
                    email
                    department
                    status
                    specialization
                    hireDate
                    salary
                    workSchedule
                    user { id username role email }
                    organization { id name }
                }
            }
        `;

        if (!staffData.name || !staffData.role || !staffData.department ||
            !staffData.organizationId || !staffData.username || !staffData.password ||
            !staffData.phone) {
            throw new Error("Missing required fields for staff creation");
        }

        const hireDateISO = staffData.hireDate ? new Date(staffData.hireDate).toISOString() : null;

        try {
            const data = await apiService.graphqlRequest(mutation, {
                name: staffData.name,
                role: staffData.role,
                specialization: staffData.specialization || null,
                department: staffData.department,
                organizationId: staffData.organizationId,
                username: staffData.username,
                password: staffData.password,
                phone: staffData.phone,
                email: staffData.email || null,
                hireDate: hireDateISO,
                salary: staffData.salary || null,
                workSchedule: staffData.workSchedule || null,
            });
            // Map role from user.role to staff.role for consistency with getStaff
            const createdStaff = data.createStaff;
            return {
                ...createdStaff,
                role: createdStaff.user?.role || null,
            };
        } catch (error) {
            console.error("Error creating staff member:", error);
            throw error;
        }
    },

    getInventoryItems: async () => {
        const query = `
            query GetInventoryItems($organizationId: Int) {
                inventoryItems(organizationId: $organizationId) {
                    id
                    name
                    itemType
                    quantity
                    unit
                    expiryDate
                    location
                    supplier
                    reorderThreshold
                    costPrice
                    sellingPrice
                    organizationId
                    isDeleted
                    status
                }
            }
        `;
        const userOrgId = localStorage.getItem('user_organization_id')
            ? parseInt(localStorage.getItem('user_organization_id'))
            : null;
        if (!userOrgId) {
            console.warn("No user_organization_id found for getInventoryItems. Returning empty array.");
            return [];
        }
        try {
            const data = await apiService.graphqlRequest(query, { organizationId: userOrgId });
            return data.inventoryItems.map(item => ({
                ...item,
                expiryDate: item.expiryDate ? new Date(item.expiryDate).toISOString().split('T')[0] : null,
            }));
        } catch (error) {
            console.error("Error fetching inventory items:", error);
            throw error;
        }
    },

    addInventoryItem: async (itemData) => {
        const mutation = `
            mutation CreateInventoryItem(
                $name: String!,
                $itemType: String!,
                $quantity: Int!,
                $unit: String,
                $expiryDate: DateTime,
                $location: String,
                $supplier: String,
                $reorderThreshold: Int,
                $costPrice: Float,
                $sellingPrice: Float,
                $organizationId: Int
            ) {
                createInventoryItem(
                    name: $name,
                    itemType: $itemType,
                    quantity: $quantity,
                    unit: $unit,
                    expiryDate: $expiryDate,
                    location: $location,
                    supplier: $supplier,
                    reorderThreshold: $reorderThreshold,
                    costPrice: $costPrice,
                    sellingPrice: $sellingPrice,
                    organizationId: $organizationId
                ) {
                    id
                    name
                    itemType
                    quantity
                    unit
                    expiryDate
                    location
                    supplier
                    reorderThreshold
                    costPrice
                    sellingPrice
                    organizationId
                    isDeleted
                }
            }
        `;
        const userOrgId = localStorage.getItem('user_organization_id')
            ? parseInt(localStorage.getItem('user_organization_id'))
            : null;
        if (!userOrgId) {
            throw new Error("Cannot add inventory item: No user_organization_id found in localStorage.");
        }
        const expiryDateISO = itemData.expiryDate ? itemData.expiryDate.replace('Z', '') : null;

        try {
            const data = await apiService.graphqlRequest(mutation, {
                ...itemData,
                expiryDate: expiryDateISO,
                supplier: itemData.supplier || null,
                reorderThreshold: itemData.reorderThreshold || null,
                costPrice: itemData.costPrice || null,
                sellingPrice: itemData.sellingPrice || null,
                organizationId: userOrgId,
            });
            return {
                ...data.createInventoryItem,
                expiryDate: itemData.expiryDate,
            };
        } catch (error) {
            console.error("Error adding inventory item:", error);
            throw error;
        }
    },

    updateInventoryItem: async (item) => {
        const mutation = `
            mutation UpdateInventoryItem(
                $id: Int!,
                $name: String,
                $itemType: String,
                $quantity: Int,
                $unit: String,
                $expiryDate: DateTime,
                $location: String,
                $supplier: String,
                $reorderThreshold: Int,
                $costPrice: Float,
                $sellingPrice: Float,
                $status: String
            ) {
                updateInventoryItem(
                    id: $id,
                    name: $name,
                    itemType: $itemType,
                    quantity: $quantity,
                    unit: $unit,
                    expiryDate: $expiryDate,
                    location: $location,
                    supplier: $supplier,
                    reorderThreshold: $reorderThreshold,
                    costPrice: $costPrice,
                    sellingPrice: $sellingPrice,
                    status: $status
                ) {
                    id
                    name
                    itemType
                    quantity
                    unit
                    expiryDate
                    location
                    supplier
                    reorderThreshold
                    costPrice
                    sellingPrice
                    status
                    organizationId
                    isDeleted
                }
            }
        `;
        
        const expiryDateISO = item.expiryDate ? new Date(item.expiryDate).toISOString() : null;
        
        try {
            const variables = {
                id: item.id,
                name: item.name,
                itemType: item.itemType,
                quantity: item.quantity,
                unit: item.unit,
                expiryDate: expiryDateISO,
                location: item.location,
                supplier: item.supplier || null,
                reorderThreshold: item.reorderThreshold || null,
                costPrice: item.costPrice || null,
                sellingPrice: item.sellingPrice || null,
                status: item.status
            };
            
            const data = await apiService.graphqlRequest(mutation, variables);
            return data.updateInventoryItem;
        } catch (error) {
            console.error("Error updating inventory item:", error);
            throw error;
        }
    },

    recordSale: async (sale) => {
        const mutation = `
            mutation CreateTransaction(
                $items: [TransactionItemInput!]!
                $paymentMethod: String!
                $patientId: String
                $organizationId: Int
            ) {
                createTransaction(
                    items: $items
                    paymentMethod: $paymentMethod
                    patientId: $patientId
                    organizationId: $organizationId
                ) {
                    id
                    items {
                        itemId
                        itemName
                        quantity
                        price
                        total
                    }
                    total
                    date
                    userId
                    currency
                    organizationId
                }
            }
        `;

        const userOrgId = localStorage.getItem('user_organization_id')
            ? parseInt(localStorage.getItem('user_organization_id'))
            : null;

        try {
            const variables = {
                items: sale.items.map(item => ({
                    inventoryItemId: item.inventoryItemId,
                    quantity: item.quantity
                })),
                paymentMethod: sale.paymentMethod,
                patientId: sale.patientId || null,
                organizationId: userOrgId
            };

            if (!variables.items || !variables.paymentMethod) {
                throw new Error("Items and payment method are required for recording a transaction");
            }

            const data = await apiService.graphqlRequest(mutation, variables);
            return data.createTransaction;
        } catch (error) {
            console.error("Error recording sale:", error);
            throw error;
        }
    },

    getMedicalRecords: async (patientId) => {
        const query = `
            query GetMedicalRecords($patientId: Int, $organizationId: Int) {
                medicalRecords(patientId: $patientId, organizationId: $organizationId) {
                    id
                    patientId
                    recordDate
                    diagnosis
                    treatment
                    medicationsPrescribed
                    notes
                    organizationId
                    isDeleted
                }
            }
        `;
        const userOrgId = localStorage.getItem('user_organization_id')
            ? parseInt(localStorage.getItem('user_organization_id'))
            : null;
        if (!userOrgId) {
            console.warn("No user_organization_id found for getMedicalRecords. Returning empty array.");
            return [];
        }
        try {
            const data = await apiService.graphqlRequest(query, { patientId, organizationId: userOrgId });
            return data.medicalRecords.map(record => ({
                ...record,
                recordDate: record.recordDate ? new Date(record.recordDate).toISOString().split('T')[0] : null,
            }));
        } catch (error) {
            console.error("Error fetching medical records:", error);
            throw error;
        }
    },

    addMedicalRecord: async (recordData) => {
        const mutation = `
            mutation CreateMedicalRecord(
                $patientId: Int!,
                $recordDate: DateTime,
                $diagnosis: String,
                $treatment: String,
                $medicationsPrescribed: String,
                $notes: String,
                $organizationId: Int
            ) {
                createMedicalRecord(
                    patientId: $patientId,
                    recordDate: $recordDate,
                    diagnosis: $diagnosis,
                    treatment: $treatment,
                    medicationsPrescribed: $medicationsPrescribed,
                    notes: $notes,
                    organizationId: $organizationId
                ) {
                    id
                    patientId
                    recordDate
                    diagnosis
                    treatment
                    medicationsPrescribed
                    notes
                    organizationId
                    isDeleted
                }
            }
        `;
        const userOrgId = localStorage.getItem('user_organization_id')
            ? parseInt(localStorage.getItem('user_organization_id'))
            : null;
        if (!userOrgId) {
            throw new Error("Cannot add medical record: No user_organization_id found in localStorage.");
        }
        const recordDateISO = recordData.recordDate ? new Date(recordData.recordDate).toISOString() : null;

        try {
            const data = await apiService.graphqlRequest(mutation, {
                ...recordData,
                recordDate: recordDateISO,
                organizationId: userOrgId,
            });
            return {
                ...data.createMedicalRecord,
                recordDate: recordData.recordDate,
            };
        } catch (error) {
            console.error("Error adding medical record:", error);
            throw error;
        }
    },

    updateMedicalRecord: async (updatedRecord) => {
        const mutation = `
            mutation UpdateMedicalRecord(
                $id: Int!,
                $diagnosis: String,
                $treatment: String,
                $medicationsPrescribed: String,
                $notes: String
            ) {
                updateMedicalRecord(
                    id: $id,
                    diagnosis: $diagnosis,
                    treatment: $treatment,
                    medicationsPrescribed: $medicationsPrescribed,
                    notes: $notes
                ) {
                    id
                    patientId
                    recordDate
                    diagnosis
                    treatment
                    medicationsPrescribed
                    notes
                    organizationId
                    isDeleted
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, updatedRecord);
            return {
                ...data.updateMedicalRecord,
                recordDate: updatedRecord.recordDate,
            };
        } catch (error) {
            console.error("Error updating medical record:", error);
            throw error;
        }
    },

    softDeleteMedicalRecord: async (id) => {
        const mutation = `
            mutation SoftDeleteMedicalRecord($id: Int!) {
                softDeleteMedicalRecord(id: $id)
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { id });
            return data.softDeleteMedicalRecord;
        } catch (error) {
            console.error("Error soft deleting medical record:", error);
            throw error;
        }
    },

    restoreMedicalRecord: async (id) => {
        const mutation = `
            mutation RestoreMedicalRecord($id: Int!) {
                restoreMedicalRecord(id: $id)
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { id });
            return data.restoreMedicalRecord;
        } catch (error) {
            console.error("Error restoring medical record:", error);
            throw error;
        }
    },

    softDeletePatient: async (id) => {
        const mutation = `
            mutation SoftDeletePatient($id: Int!) {
                softDeletePatient(id: $id)
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { id });
            return data.softDeletePatient;
        } catch (error) {
            console.error("Error soft deleting patient:", error);
            throw error;
        }
    },

    restorePatient: async (id) => {
        const mutation = `
            mutation RestorePatient($id: Int!) {
                restorePatient(id: $id)
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { id });
            return data.restorePatient;
        } catch (error) {
            console.error("Error restoring patient:", error);
            throw error;
        }
    },

    softDeleteStaff: async (id) => {
        const mutation = `
            mutation SoftDeleteStaff($id: Int!) {
                softDeleteStaff(id: $id)
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { id });
            return data.softDeleteStaff;
        } catch (error) {
            console.error("Error soft deleting staff:", error);
            throw error;
        }
    },

    restoreStaff: async (id) => {
        const mutation = `
            mutation RestoreStaff($id: Int!) {
                restoreStaff(id: $id)
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { id });
            return data.restoreStaff;
        } catch (error) {
            console.error("Error restoring staff:", error);
            throw error;
        }
    },

    softDeleteAppointment: async (id) => {
        const mutation = `
            mutation SoftDeleteAppointment($id: Int!) {
                softDeleteAppointment(id: $id)
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { id });
            return data.softDeleteAppointment;
        } catch (error) {
            console.error("Error soft deleting appointment:", error);
            throw error;
        }
    },

    restoreAppointment: async (id) => {
        const mutation = `
            mutation RestoreAppointment($id: Int!) {
                restoreAppointment(id: $id)
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { id });
            return data.restoreAppointment;
        } catch (error) {
            console.error("Error restoring appointment:", error);
            throw error;
        }
    },

    softDeleteInventoryItem: async (id) => {
        const mutation = `
            mutation SoftDeleteInventoryItem($id: Int!) {
                softDeleteInventoryItem(id: $id)
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { id });
            return data.softDeleteInventoryItem;
        } catch (error) {
            console.error("Error soft deleting inventory item:", error);
            throw error;
        }
    },

    restoreInventoryItem: async (id) => {
        const mutation = `
            mutation RestoreInventoryItem($id: Int!) {
                restoreInventoryItem(id: $id)
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { id });
            return data.restoreInventoryItem;
        } catch (error) {
            console.error("Error restoring inventory item:", error);
            throw error;
        }
    },

    softDeleteOrganization: async (id) => {
        const mutation = `
            mutation SoftDeleteOrganization($id: Int!) {
                softDeleteOrganization(id: $id)
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { id });
            return data.softDeleteOrganization;
        } catch (error) {
            console.error("Error soft deleting organization:", error);
            throw error;
        }
    },

    restoreOrganization: async (id) => {
        const mutation = `
            mutation RestoreOrganization($id: Int!) {
                restoreOrganization(id: $id)
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { id });
            return data.restoreOrganization;
        } catch (error) {
            console.error("Error restoring organization:", error);
            throw error;
        }
    },

    softDeleteUser: async (id) => {
        const mutation = `
            mutation SoftDeleteUser($id: Int!) {
                softDeleteUser(id: $id)
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { id });
            return data.softDeleteUser;
        } catch (error) {
            console.error("Error soft deleting user:", error);
            throw error;
        }
    },

    restoreUser: async (id) => {
        const mutation = `
            mutation RestoreUser($id: Int!) {
                restoreUser(id: $id)
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { id });
            return data.restoreUser;
        } catch (error) {
            console.error("Error restoring user:", error);
            throw error;
        }
    },

    analyzeSymptomsLLM: async (symptoms) => {
        await apiService.simulateNetworkDelay();
        const prompt = `Analyze the following patient symptoms and suggest the most likely hospital department they should be routed to, along with a brief potential diagnosis. Symptoms: "${symptoms}" Format your response as: Department: [Suggested Department]\nPotential Diagnosis: [Brief Diagnosis]`;
        try {
            let chatHistory = [];
            chatHistory.push({ role: "user", parts: [{ text: prompt }] });
            const payload = { contents: chatHistory };
            const apiKey = process.env.REACT_APP_GEMINI_API_KEY || "";
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const result = await response.json();
            if (result.candidates && result.candidates.length > 0 && result.candidates[0].content && result.candidates[0].content.parts && result.candidates[0].content.parts.length > 0) {
                return result.candidates[0].content.parts[0].text;
            } else {
                console.error('Unexpected LLM API response structure:', result);
                return 'Could not generate a recommendation. Please try again.';
            }
        } catch (error) {
            console.error('Error calling Gemini API:', error);
            return 'Error analyzing symptoms. Please check your network connection or try again later.';
        }
    },

    getMedicationInfoLLM: async (medicationName) => {
        await apiService.simulateNetworkDelay();
        const prompt = `Provide a brief overview, common side effects, and potential common drug interactions for the medication: "${medicationName}". Format your response clearly with headings for each section (Overview, Side Effects, Drug Interactions).`;
        try {
            let chatHistory = [];
            chatHistory.push({ role: "user", parts: [{ text: prompt }] });
            const payload = { contents: chatHistory };
            const apiKey = process.env.REACT_APP_GEMINI_API_KEY || "";
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const result = await response.json();
            if (result.candidates && result.candidates.length > 0 && result.candidates[0].content && result.candidates[0].content.parts && result.candidates[0].content.parts.length > 0) {
                return result.candidates[0].content.parts[0].text;
            } else {
                console.error('Unexpected LLM API response structure:', result);
                return 'Could not retrieve medication info. Please try again.';
            }
        } catch (error) {
            console.error('Error calling Gemini API:', error);
            return 'Error retrieving medication info. Please check your network connection or try again later.';
        }
    },

    // ==================== PASSWORD MANAGEMENT ====================
    
    // Change password for logged-in user
    changePassword: async (currentPassword, newPassword) => {
        const query = `
            mutation ChangePassword($currentPassword: String!, $newPassword: String!) {
                changePassword(currentPassword: $currentPassword, newPassword: $newPassword) {
                    success
                    message
                }
            }
        `;
        return apiService.graphqlRequest(query, { currentPassword, newPassword });
    },

    // Request password reset
    requestPasswordReset: async (email) => {
        const query = `
            mutation RequestPasswordReset($email: String!) {
                requestPasswordReset(email: $email) {
                    success
                    message
                }
            }
        `;
        return apiService.graphqlRequest(query, { email });
    },

    // Reset password with token
    resetPassword: async (token, newPassword) => {
        const query = `
            mutation ResetPassword($token: String!, $newPassword: String!) {
                resetPassword(token: $token, newPassword: $newPassword) {
                    success
                    message
                }
            }
        `;
        return apiService.graphqlRequest(query, { token, newPassword });
    },

    // ==================== ACCOUNT REQUEST ====================
    
    // Request account creation
    requestAccountCreation: async (requestData) => {
        const query = `
            mutation RequestAccountCreation(
                $organizationName: String!
                $contactName: String!
                $email: String!
                $phone: String!
                $organizationType: String!
                $message: String
            ) {
                requestAccountCreation(
                    organizationName: $organizationName
                    contactName: $contactName
                    email: $email
                    phone: $phone
                    organizationType: $organizationType
                    message: $message
                ) {
                    success
                    message
                }
            }
        `;
        return apiService.graphqlRequest(query, requestData);
    },

    // ==================== NOTIFICATIONS ====================
    
    // Get notifications for current user
    getNotifications: async (unreadOnly = false, limit = 50) => {
        const query = `
            query GetNotifications($unreadOnly: Boolean!, $limit: Int!) {
                notifications(unreadOnly: $unreadOnly, limit: $limit) {
                    id
                    userId
                    organizationId
                    type
                    title
                    message
                    priority
                    read
                    dismissed
                    actionUrl
                    actionText
                    createdAt
                    readAt
                }
            }
        `;
        return apiService.graphqlRequest(query, { unreadOnly, limit });
    },

    // Get unread notification count
    getUnreadNotificationCount: async () => {
        const query = `
            query {
                unreadNotificationCount
            }
        `;
        return apiService.graphqlRequest(query);
    },

    // Mark notification as read
    markNotificationRead: async (notificationId) => {
        const query = `
            mutation MarkNotificationRead($notificationId: Int!) {
                markNotificationRead(notificationId: $notificationId)
            }
        `;
        return apiService.graphqlRequest(query, { notificationId });
    },

    // Mark all notifications as read
    markAllNotificationsRead: async () => {
        const query = `
            mutation {
                markAllNotificationsRead
            }
        `;
        return apiService.graphqlRequest(query);
    },

    // Dismiss notification
    dismissNotification: async (notificationId) => {
        const query = `
            mutation DismissNotification($notificationId: Int!) {
                dismissNotification(notificationId: $notificationId)
            }
        `;
        return apiService.graphqlRequest(query, { notificationId });
    },

    // ==================== QUEUE MANAGEMENT ====================
    
    // Assign queue number on check-in
    assignQueueNumber: async (patientId) => {
        const query = `
            mutation AssignQueueNumber($patientId: Int!) {
                assignQueueNumber(patientId: $patientId) {
                    success
                    message
                    queueNumber
                    location
                }
            }
        `;
        return apiService.graphqlRequest(query, { patientId });
    },

    // Update patient location
    updatePatientLocation: async (patientId, location, roomNumber = null, nextLocation = null) => {
        const query = `
            mutation UpdatePatientLocation($patientId: Int!, $location: String!, $roomNumber: String, $nextLocation: String) {
                updatePatientLocation(
                    patientId: $patientId
                    location: $location
                    roomNumber: $roomNumber
                    nextLocation: $nextLocation
                ) {
                    success
                    message
                    queueNumber
                    location
                }
            }
        `;
        return apiService.graphqlRequest(query, { patientId, location, roomNumber, nextLocation });
    },

    // Call next patient
    callNextPatient: async (location, roomNumber = null, doctorId = null) => {
        const query = `
            mutation CallNextPatient($location: String!, $roomNumber: String, $doctorId: Int) {
                callNextPatient(location: $location, roomNumber: $roomNumber, doctorId: $doctorId) {
                    success
                    message
                    queueNumber
                    location
                }
            }
        `;
        return apiService.graphqlRequest(query, { location, roomNumber, doctorId });
    },

    // Get queue by location (for display boards)
    getQueueByLocation: async (location) => {
        const query = `
            query QueueByLocation($location: String!) {
                queueByLocation(location: $location) {
                    location
                    nowServing {
                        id
                        patientId
                        name
                        queueNumber
                        currentLocation
                        nextLocation
                        roomNumber
                        status
                        department
                    }
                    waitingQueue {
                        id
                        patientId
                        name
                        queueNumber
                        currentLocation
                        nextLocation
                        status
                        department
                    }
                    totalWaiting
                }
            }
        `;
        return apiService.graphqlRequest(query, { location });
    },

    // Get all queues (complete overview)
    getAllQueues: async () => {
        const query = `
            query {
                allQueues {
                    location
                    nowServing {
                        id
                        name
                        queueNumber
                        roomNumber
                    }
                    waitingQueue {
                        id
                        name
                        queueNumber
                    }
                    totalWaiting
                }
            }
        `;
        return apiService.graphqlRequest(query);
    },

    // Get today's complete queue
    getTodaysQueue: async () => {
        const query = `
            query {
                todaysQueue {
                    id
                    patientId
                    name
                    queueNumber
                    currentLocation
                    nextLocation
                    roomNumber
                    status
                    checkInTime
                    department
                }
            }
        `;
        return apiService.graphqlRequest(query);
    },

    // Search user by patient ID
    getUserByPatientId: async (patientId) => {
        const query = `
            query GetUserByPatientId($patientId: String!) {
                userByPatientId(patientId: $patientId) {
                    id
                    username
                    email
                    role
                    isActive
                    organizationId
                    organization {
                        id
                        name
                        orgType
                    }
                }
            }
        `;
        
        const variables = { patientId };
        
        try {
            const data = await apiService.graphqlRequest(query, variables);
            return data.userByPatientId;
        } catch (error) {
            console.error('Error fetching user by patient ID:', error);
            throw error;
        }
    },

    // ===== Department Management =====
    
    // Get all departments for organization
    getDepartments: async () => {
        const query = `
            query {
                activeDepartments {
                    id
                    name
                    description
                    isActive
                    createdAt
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(query);
            return data.activeDepartments || [];
        } catch (error) {
            console.error('Error fetching departments:', error);
            return [];
        }
    },

    // Create a new department
    createDepartment: async (name, description = null) => {
        const mutation = `
            mutation CreateDepartment($input: DepartmentInput!) {
                createDepartment(departmentInput: $input) {
                    success
                    message
                    department {
                        id
                        name
                        description
                        isActive
                    }
                }
            }
        `;
        
        const variables = {
            input: {
                name: name,
                description: description,
                isActive: true
            }
        };
        
        try {
            const data = await apiService.graphqlRequest(mutation, variables);
            return data.createDepartment;
        } catch (error) {
            console.error('Error creating department:', error);
            throw error;
        }
    },

    // Update department
    updateDepartment: async (id, name, description = null) => {
        const mutation = `
            mutation UpdateDepartment($id: Int!, $input: DepartmentInput!) {
                updateDepartment(departmentId: $id, departmentInput: $input) {
                    success
                    message
                    department {
                        id
                        name
                        description
                        isActive
                    }
                }
            }
        `;
        
        const variables = {
            id: id,
            input: {
                name: name,
                description: description
            }
        };
        
        try {
            const data = await apiService.graphqlRequest(mutation, variables);
            return data.updateDepartment;
        } catch (error) {
            console.error('Error updating department:', error);
            throw error;
        }
    },

    // Delete department
    deleteDepartment: async (id) => {
        const mutation = `
            mutation DeleteDepartment($id: Int!) {
                deleteDepartment(departmentId: $id) {
                    success
                    message
                }
            }
        `;
        
        const variables = { id: id };
        
        try {
            const data = await apiService.graphqlRequest(mutation, variables);
            return data.deleteDepartment;
        } catch (error) {
            console.error('Error deleting department:', error);
            throw error;
        }
    },

    // ============================================
    // PAGINATED QUERIES FOR SCALABILITY
    // ============================================

    // Get paginated patients with search
    getPatientsPaginated: async ({ page = 1, pageSize = 50, searchTerm = null, searchField = null } = {}) => {
        const query = `
            query GetPatientsPaginated($page: Int!, $pageSize: Int!, $searchTerm: String, $searchField: String) {
                patientsPaginated(page: $page, pageSize: $pageSize, searchTerm: $searchTerm, searchField: $searchField) {
                    items {
                        id
                        patientId
                        name
                        age
                        phone
                        email
                        address
                        gender
                        emergencyContact
                        department
                        symptoms
                        medicalHistory
                        organizationId
                        isDeleted
                        status
                        queueNumber
                        currentLocation
                        nextLocation
                        roomNumber
                        checkInTime
                        lastUpdated
                    }
                    total
                    page
                    pageSize
                    hasMore
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(query, { page, pageSize, searchTerm, searchField });
            return data.patientsPaginated;
        } catch (error) {
            console.error('Error fetching paginated patients:', error);
            throw error;
        }
    },

    // Get paginated staff with search
    getStaffPaginated: async ({ page = 1, pageSize = 50, searchTerm = null, searchField = null, role = null } = {}) => {
        const query = `
            query GetStaffPaginated($page: Int!, $pageSize: Int!, $searchTerm: String, $searchField: String, $role: String) {
                staffMembersPaginated(page: $page, pageSize: $pageSize, searchTerm: $searchTerm, searchField: $searchField, role: $role) {
                    items {
                        id
                        userId
                        fullName
                        department
                        position
                        phone
                        email
                        specialization
                        isDeleted
                        organizationId
                    }
                    total
                    page
                    pageSize
                    hasMore
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(query, { page, pageSize, searchTerm, searchField, role });
            return data.staffMembersPaginated;
        } catch (error) {
            console.error('Error fetching paginated staff:', error);
            throw error;
        }
    },

    // Get paginated appointments with search
    getAppointmentsPaginated: async ({ page = 1, pageSize = 50, searchTerm = null, status = null } = {}) => {
        const query = `
            query GetAppointmentsPaginated($page: Int!, $pageSize: Int!, $searchTerm: String, $status: String) {
                appointmentsPaginated(page: $page, pageSize: $pageSize, searchTerm: $searchTerm, status: $status) {
                    items {
                        id
                        patientId
                        doctorId
                        appointmentTime
                        department
                        appointmentType
                        status
                        notes
                        organizationId
                        isDeleted
                    }
                    total
                    page
                    pageSize
                    hasMore
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(query, { page, pageSize, searchTerm, status });
            return data.appointmentsPaginated;
        } catch (error) {
            console.error('Error fetching paginated appointments:', error);
            throw error;
        }
    },

    // Get paginated inventory with search
    getInventoryPaginated: async ({ page = 1, pageSize = 50, searchTerm = null, searchField = null, lowStockThreshold = null } = {}) => {
        const query = `
            query GetInventoryPaginated($page: Int!, $pageSize: Int!, $searchTerm: String, $searchField: String, $lowStockThreshold: Int) {
                inventoryItemsPaginated(page: $page, pageSize: $pageSize, searchTerm: $searchTerm, searchField: $searchField, lowStockThreshold: $lowStockThreshold) {
                    items {
                        id
                        name
                        category
                        quantity
                        unitPrice
                        reorderLevel
                        description
                        organizationId
                        isDeleted
                    }
                    total
                    page
                    pageSize
                    hasMore
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(query, { page, pageSize, searchTerm, searchField, lowStockThreshold });
            return data.inventoryItemsPaginated;
        } catch (error) {
            console.error('Error fetching paginated inventory:', error);
            throw error;
        }
    },

    // ===== Location Management =====
    
    // Get all locations
    getLocations: async (type = null) => {
        const query = `
            query GetLocations($type: String) {
                locations(type: $type) {
                    location
                    nowServing {
                        id
                        patientId
                        name
                        queueNumber
                        currentLocation
                        nextLocation
                        roomNumber
                        status
                        checkInTime
                        department
                    }
                    waitingQueue {
                        id
                        patientId
                        name
                        queueNumber
                        currentLocation
                        nextLocation
                        roomNumber
                        status
                        checkInTime
                        department
                    }
                    totalWaiting
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(query);
            return data;
        } catch (error) {
            console.error('Error fetching all queues:', error);
            throw error;
        }
    },

    // Get today's queue (all patients checked in today)
    getTodaysQueue: async () => {
        const query = `
            query GetTodaysQueue {
                todaysQueue {
                    id
                    patientId
                    name
                    queueNumber
                    currentLocation
                    nextLocation
                    roomNumber
                    status
                    checkInTime
                    department
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(query);
            return data;
        } catch (error) {
            console.error('Error fetching today\'s queue:', error);
            throw error;
        }
    },

    // Get queue for specific location
    getQueueByLocation: async (location) => {
        const query = `
            query GetQueueByLocation($location: String!) {
                queueByLocation(location: $location) {
                    location
                    nowServing {
                        id
                        patientId
                        name
                        queueNumber
                        currentLocation
                        nextLocation
                        roomNumber
                        status
                        checkInTime
                        department
                    }
                    waitingQueue {
                        id
                        patientId
                        name
                        queueNumber
                        currentLocation
                        nextLocation
                        roomNumber
                        status
                        checkInTime
                        department
                    }
                    totalWaiting
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(query, { location });
            return data.queueByLocation;
        } catch (error) {
            console.error('Error fetching queue by location:', error);
            throw error;
        }
    },

    // Assign queue number to patient (on check-in)
    assignQueueNumber: async (patientId) => {
        const mutation = `
            mutation AssignQueueNumber($patientId: Int!) {
                assignQueueNumber(patientId: $patientId) {
                    success
                    message
                    queueNumber
                    location
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { patientId });
            return data.assignQueueNumber;
        } catch (error) {
            console.error('Error assigning queue number:', error);
            throw error;
        }
    },

    // Update patient location
    updatePatientLocation: async (patientId, location, roomNumber = null, nextLocation = null) => {
        const mutation = `
            mutation UpdatePatientLocation($patientId: Int!, $location: String!, $roomNumber: String, $nextLocation: String) {
                updatePatientLocation(patientId: $patientId, location: $location, roomNumber: $roomNumber, nextLocation: $nextLocation) {
                    success
                    message
                    queueNumber
                    location
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { 
                patientId, 
                location, 
                roomNumber: roomNumber || null, 
                nextLocation: nextLocation || null 
            });
            return data.updatePatientLocation;
        } catch (error) {
            console.error('Error updating patient location:', error);
            throw error;
        }
    },

    // Call next patient in queue
    callNextPatient: async (location, roomNumber = null, doctorId = null) => {
        const mutation = `
            mutation CallNextPatient($location: String!, $roomNumber: String, $doctorId: Int) {
                callNextPatient(location: $location, roomNumber: $roomNumber, doctorId: $doctorId) {
                    success
                    message
                    queueNumber
                    location
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { 
                location, 
                roomNumber: roomNumber || null, 
                doctorId: doctorId || null 
            });
            return data;
        } catch (error) {
            console.error('Error calling next patient:', error);
            throw error;
        }
    },

    // ===== Location Management =====
    
    // Get all locations
    getLocations: async (type = null) => {
        const query = `
            query GetLocations($type: String) {
                locations(type: $type) {
                    id
                    organizationId
                    type
                    name
                    shortName
                    roomNumber
                    floor
                    wing
                    building
                    fullAddress
                    assignedStaffId
                    department
                    capacity
                    currentOccupancy
                    isActive
                    isUnderMaintenance
                    showOnDisplay
                    icon
                    colorCode
                    displayOrder
                    equipment
                    specialNotes
                    createdAt
                    updatedAt
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(query, { type });
            return data.locations || [];
        } catch (error) {
            console.error('Error fetching locations:', error);
            return [];
        }
    },

    // Get available rooms by type
    getAvailableRooms: async (type) => {
        const query = `
            query GetAvailableRooms($type: String!) {
                availableRooms(type: $type) {
                    id
                    name
                    shortName
                    roomNumber
                    fullAddress
                    assignedStaffId
                    capacity
                    currentOccupancy
                    isActive
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(query, { type });
            return data.availableRooms || [];
        } catch (error) {
            console.error('Error fetching available rooms:', error);
            return [];
        }
    },

    // Create location
    createLocation: async (locationData) => {
        const mutation = `
            mutation CreateLocation($input: LocationInput!) {
                createLocation(input: $input) {
                    id
                    name
                    type
                    fullAddress
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { input: locationData });
            return data.createLocation;
        } catch (error) {
            console.error('Error creating location:', error);
            throw error;
        }
    },

    // Update location
    updateLocation: async (locationId, locationData) => {
        const mutation = `
            mutation UpdateLocation($locationId: Int!, $input: LocationInput!) {
                updateLocation(locationId: $locationId, input: $input) {
                    id
                    name
                    type
                    fullAddress
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { locationId, input: locationData });
            return data.updateLocation;
        } catch (error) {
            console.error('Error updating location:', error);
            throw error;
        }
    },

    // Delete location
    deleteLocation: async (locationId) => {
        const mutation = `
            mutation DeleteLocation($locationId: Int!) {
                deleteLocation(locationId: $locationId)
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { locationId });
            return data.deleteLocation;
        } catch (error) {
            console.error('Error deleting location:', error);
            throw error;
        }
    },

    // Set staff location
    setStaffLocation: async (staffId, locationId, customLocation = null) => {
        const mutation = `
            mutation SetStaffLocation($input: SetStaffLocationInput!) {
                setStaffLocation(input: $input) {
                    id
                    staffId
                    roomName
                    isAvailable
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, {
                input: {
                    staffId,
                    locationId,
                    customLocation,
                    isAvailable: true
                }
            });
            return data.setStaffLocation;
        } catch (error) {
            console.error('Error setting staff location:', error);
            throw error;
        }
    },

    // Get staff current location
    getStaffLocation: async (staffId = null) => {
        const query = `
            query GetStaffLocation($staffId: Int) {
                staffCurrentLocation(staffId: $staffId) {
                    id
                    staffId
                    locationId
                    roomName
                    isAvailable
                    isOnBreak
                    customLocation
                    currentQueueNumber
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(query, { staffId });
            return data.staffCurrentLocation;
        } catch (error) {
            console.error('Error fetching staff location:', error);
            return null;
        }
    },

    // ===== Patient Routing =====
    
    // Complete triage and route patient
    completeTriageAndRoute: async (input) => {
        const mutation = `
            mutation CompleteTriageAndRoute($input: CompleteTriageInput!) {
                completeTriageAndRoute(input: $input) {
                    success
                    message
                    nextStep
                    nextRoom
                    fullAddress
                    queueNumber
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { input });
            return data.completeTriageAndRoute;
        } catch (error) {
            console.error('Error completing triage:', error);
            throw error;
        }
    },

    // Complete consultation with multi-step routing
    completeConsultationAndRoute: async (input) => {
        const mutation = `
            mutation CompleteConsultationAndRoute($input: CompleteConsultationInput!) {
                completeConsultationAndRoute(input: $input) {
                    success
                    message
                    nextStep
                    nextRoom
                    queueNumber
                    journeySteps
                }
            }
        `;
        try {
            const data = await apiService.graphqlRequest(mutation, { input });
            return data.completeConsultationAndRoute;
        } catch (error) {
            console.error('Error completing consultation:', error);
            throw error;
        }
    },
};

export default apiService;